import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Heart, ThumbsDown, Sparkles, X, RotateCcw } from "lucide-react";

const TRAIT_LABEL = {
  curiosité: "Curiosité",
  humour: "Humour",
  chaleur: "Chaleur",
  franchise: "Franchise",
  énergie: "Énergie",
  créativité: "Créativité",
};

export function PersonalityPanel({ open }) {
  const [data, setData] = useState({ traits: {}, tastes: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/personality")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const removeTaste = async (id) => {
    setData((d) => ({ ...d, tastes: d.tastes.filter((t) => t.id !== id) }));
    try {
      await api.delete(`/personality/tastes/${id}`);
    } catch (_) {
      toast.error("Échec de la suppression");
      load();
    }
  };

  const reset = async () => {
    try {
      const { data } = await api.post("/personality/reset");
      setData(data);
      toast.success("Personnalité de Sara réinitialisée");
    } catch (_) {
      toast.error("Échec de la réinitialisation");
    }
  };

  const likes = (data.tastes || []).filter((t) => t.kind === "like");
  const dislikes = (data.tastes || []).filter((t) => t.kind === "dislike");
  const traits = Object.entries(data.traits || {});

  return (
    <div data-testid="personality-panel" className="flex flex-col h-full overflow-y-auto px-1 py-2 space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 accent-text" />
          <span className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">TRAITS DE CARACTÈRE</span>
        </div>
        <div className="space-y-3">
          {traits.map(([key, val]) => (
            <div key={key} data-testid={`trait-${key}`}>
              <div className="flex justify-between font-mono-hud text-[10px] text-[#c8d6e5] mb-1">
                <span>{TRAIT_LABEL[key] || key}</span>
                <span className="accent-text">{val}/100</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${val}%`, background: "linear-gradient(90deg, hsl(var(--hue),100%,45%), hsl(var(--hue),100%,68%))" }}
                />
              </div>
            </div>
          ))}
          {!traits.length && !loading && (
            <p className="font-mono-hud text-[11px] text-[#556]">Personnalité par défaut.</p>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Heart className="h-4 w-4 text-[#4ade80]" />
          <span className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">CE QUE SARA AIME</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {likes.map((t) => (
            <span
              key={t.id}
              data-testid="taste-like"
              className="group inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border border-[rgba(74,222,128,0.35)] bg-[rgba(74,222,128,0.08)] text-[12px] text-[#d6ffe4]"
            >
              {t.text}
              <button onClick={() => removeTaste(t.id)} aria-label="Oublier" className="opacity-50 group-hover:opacity-100 hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {!likes.length && <span className="font-mono-hud text-[11px] text-[#556]">Rien encore — ça viendra au fil des échanges.</span>}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <ThumbsDown className="h-4 w-4 text-[#f87171]" />
          <span className="font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">CE QUE SARA N'AIME PAS</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {dislikes.map((t) => (
            <span
              key={t.id}
              data-testid="taste-dislike"
              className="group inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border border-[rgba(248,113,113,0.35)] bg-[rgba(248,113,113,0.08)] text-[12px] text-[#ffe0e0]"
            >
              {t.text}
              <button onClick={() => removeTaste(t.id)} aria-label="Oublier" className="opacity-50 group-hover:opacity-100 hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {!dislikes.length && <span className="font-mono-hud text-[11px] text-[#556]">Rien encore.</span>}
        </div>
      </div>

      <button
        data-testid="personality-reset-button"
        onClick={reset}
        className="mt-auto flex items-center justify-center gap-2 w-full py-2.5 border border-[var(--border-hud)] clip-corner text-[#8b949e] hover:text-white hover:bg-[var(--accent-a10)] transition-colors font-mono-hud text-[10px] tracking-[0.2em] uppercase"
      >
        <RotateCcw className="h-3.5 w-3.5" /> Réinitialiser la personnalité
      </button>
      <p className="font-mono-hud text-[9px] text-[#556] leading-relaxed">
        La personnalité de Sara évolue naturellement au fil de vos conversations. Vous pouvez oublier un goût ou tout réinitialiser.
      </p>
    </div>
  );
}
